import { Box, Chip, Typography, useTheme } from "@mui/material";
import React from "react";
import type {
    PassCriterion,
    ResearchQuestionValidation,
    ScoredCriterion,
} from "models/ResearchFindings/ResearchFindings";

type Props = {
    validation: ResearchQuestionValidation;
};

type Criterion = {
    label: string;
    passed: boolean;
    detail?: string;
};

function scored(label: string, c?: ScoredCriterion): Criterion | null {
    if (!c) return null;
    return { label: `${label} ${c.score}/3`, passed: c.score >= 2, detail: c.reason };
}

function pass(label: string, c?: PassCriterion): Criterion | null {
    if (!c) return null;
    return { label, passed: c.passed, detail: c.reason };
}

/** The "why this passed" validation block for a question. */
const WhyPassedSection: React.FC<Props> = ({ validation }) => {
    const theme = useTheme();

    const criteria: Criterion[] = [
        scored("Alignment", validation.alignment),
        scored("Actionability", validation.actionability),
        pass("Feasibility", validation.feasibility),
        pass("Specificity", validation.specificity),
    ].filter((c): c is Criterion => c !== null);

    return (
        <Box
            sx={{
                mt: 1.5,
                padding: theme.spacing(1.5),
                borderRadius: "8px",
                backgroundColor: theme.palette.gray[100],
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography
                    sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: theme.palette.gray[600],
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                    }}
                >
                    Why this passed
                </Typography>
                {validation.lever && (
                    <Chip
                        size="small"
                        color="success"
                        label="High leverage"
                        variant="outlined"
                    />
                )}
            </Box>

            {validation.reason && (
                <Typography
                    sx={{ fontSize: "13px", color: theme.palette.gray[700], mb: 1 }}
                >
                    {validation.reason}
                </Typography>
            )}

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {criteria.map((c) => (
                    <Chip
                        key={c.label}
                        size="small"
                        label={c.label}
                        color={c.passed ? "success" : "default"}
                        variant="outlined"
                        title={c.detail}
                    />
                ))}
            </Box>

            {validation.lever && (
                <Typography
                    sx={{ fontSize: "12px", color: theme.palette.gray[500], mt: 1 }}
                >
                    Lever: {validation.lever}
                </Typography>
            )}
        </Box>
    );
};

export default WhyPassedSection;
